import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import moment from 'moment'
import DataWorker from 'util/data.worker'
import { serialize, deepCopy } from 'util/helpers'

Vue.use(Vuex)

// set worker
const worker = new DataWorker()

export default new Vuex.Store({
  state: {
    config: {},
    status: '',
    token: window.localStorage.getItem(window.app.id + '.login.token') || '',
    user: window.localStorage.getItem(window.app.id + '.login.user') || '',
    baseLayer: null,
    tileSetConf: null,
    tileSetProperty: null,
    searchResults: [],
    parcel: null,
    parcelStatus: null,
    parcelEvents: [],
    parcelLastRow: null,
    feature: null,
    timebar_value: null,
    filter: {
      0: true,
      1: true,
      2: true
    },
    chartData: null,
    chartDate: null,
    about: false,
    searchIndex: 0
  },
  mutations: {
    config (state, config) {
      state.config = config
    },
    status (state, value) {
      state.status = value
    },
    auth_success (state, user) {
      state.token = user.token
      state.user = user.username
    },
    logout (state) {
      state.status = ''
      state.token = ''
      state.user = ''
      state.parcel = null
      state.feature = null
    },
    baseLayerChanged (state, layer) {
      state.baseLayer = layer
    },
    tileSetChanged (state, conf) {
      state.tileSetConf = conf
    },
    tileSetPropertyChanged (state, set) {
      state.tileSetProperty = set
    },
    search_success (state, results) {
      state.searchResults = results
    },
    parcel_status (state, status) {
      state.parcelStatus = status
    },
    parcel_events (state, events) {
      state.parcelEvents = events
    },
    parcel_last_row (state, row) {
      state.parcelLastRow = row
    },
    feature (state, feature) {
      state.feature = feature
    },
    timebar (state, value) {
      state.timebar_value = value
    },
    filter (state, value) {
      state.filter = value
    },
    chartData (state, result) {
      state.chartData = result
    },
    chartDate (state, value) {
      state.chartDate = value
    },
    about (state, value) {
      state.about = value
    },
    parcel (state, row) {
      state.parcel = row
    },
    searchIndex (state, idx) {
      state.searchIndex = idx
    }
  },
  actions: {
    config ({ commit }, config) {
      commit('config', config)
    },
    status ({ commit, state}) {
      return new Promise((resolve, reject) => {
        axios.get(state.config.api.url + 'status')
          .then(resp => {
            resolve(resp)
          }).catch(err => {
            reject(err)
          })
      })
    },
    login ({ commit, state }, user) {
      return new Promise((resolve, reject) => {
        const token = 'Basic ' + btoa(user.username + ':' + user.password)
        axios.get(state.config.api.url + 'status', {
          withCredentials: true,
          auth: user
        }).then(resp => {
          if (user.rememberme) {
            window.localStorage.setItem(window.app.id + '.login.token', token)
            window.localStorage.setItem(window.app.id + '.login.user', user.username)
            window.localStorage.setItem(window.app.id + '.login.expire', (Math.floor(Date.now() / 1000) + (state.config.api.expire * 3600)))
          }
          axios.defaults.headers.common['Authorization'] = token
          commit('auth_success', { username: user.username, token: token })
          resolve(resp)
        }).catch(err => {
          window.localStorage.removeItem(window.app.id + '.login.token')
          window.localStorage.removeItem(window.app.id + '.login.user')
          window.localStorage.removeItem(window.app.id + '.login.expire')
          reject(err)
        })
      })
    },
    logout ({ commit }) {
      return new Promise((resolve, reject) => {
        commit('logout')
        window.localStorage.removeItem(window.app.id + '.login.token')
        window.localStorage.removeItem(window.app.id + '.login.user')
        window.localStorage.removeItem(window.app.id + '.login.expire')
        delete axios.defaults.headers.common['Authorization']
        resolve()
      })
    },
    setBaseLayer ({ commit, state }, layer) {
      commit('baseLayerChanged', layer)
    },
    loadTileSetConf ({ commit, state }, tileset) {
      return new Promise((resolve, reject) => {
        axios.get(state.config.tilesets.url + state.config.tilesets.key + '/' + tileset + '/metadata.json')
          .then(resp => {
            resp.data.id = tileset
            resp.data.bounds = resp.data.bounds.split(',').map(c => Number(c))
            resp.data.center = resp.data.center.split(',').map(c => Number(c))
            resp.data.minzoom = Number(resp.data.minzoom)
            resp.data.maxzoom = Number(resp.data.maxzoom)
            commit('tileSetChanged', resp.data)
            resolve(resp.data)
          }).catch(err => {
            reject(err)
          })
      })
    },
    changeTileSetProperty ({ commit, state }, set) {
      commit('tileSetPropertyChanged', set)
    },
    search ({ commit, state }, input) {
      commit('searchIndex', input.index)
      return new Promise((resolve, reject) => {
        const searchConf = state.config.search[input.index]
        const params = deepCopy(state.config.api.endpoints[searchConf.request])
        params[searchConf.query] = input.query
        Object.keys(params).forEach(key => {
          if (params[key] === null) {
            delete params[key]
          }
        })
        axios.get(state.config.api.url + searchConf.request + '?' + serialize(params))
          .then(resp => {
            if (resp.data && resp.data.length) {
              // geometry text to json
              resp.data.forEach(row => {
                if (typeof row.geometry === 'string') {
                  row.geometry = JSON.parse(row.geometry)
                }
              })
              if (resp.data.length === 1) {
                commit('parcel', resp.data[0])
              } else {
                commit('search_success', resp.data)
              }
              resolve(resp.data)
            } else {
              reject()
            }
          }).catch(err => {
            reject(err)
          })
      })
    },
    selectedFeature ({ commit, state }, feature) {
      commit('feature', feature)
    },
    clearSearch ({ commit, state }) {
      commit('search_success', [])
      commit('feature', null)
      commit('parcel_status', null)
      commit('parcel_events', [])
      commit('parcel', null)
    },
    timebar ({ commit, state }, value) {
      commit('timebar', value)
    },
    filter ({ commit, state }, filter) {
      commit('filter', filter)
    },
    loadParcelEvents ({ commit, state }, id) {
      const request = 'parcel_estimate'
      return new Promise((resolve, reject) => {
        const params = deepCopy(state.config.api.endpoints[request])
        params.parcel_foreign_id = id
        params.changes_only = 'true'
        params.updates_from = state.config.timebar.minDate
        axios.get(state.config.api.url + request + '?' + serialize(params))
          .then(resp => {
            if (resp.data && resp.data.length) {
              const data = resp.data
                .filter(row => row.estimate === 'cut')
                .sort((a, b) => {
                  return moment(a.s1product_end_time).diff(b.s1product_end_time)
                })
                .map(row => {
                  if (row.s1product_end_time) {
                    row.date = moment(row.s1product_end_time).format('YYYY-MM-DD')
                  }
                  return row
                })
              commit('parcel_events', data)
              resolve(data)
            } else {
              reject()
            }
          }).catch(err => {
            reject(err)
          })
      })
    },
    loadParcelStatus ({ commit, state }, id) {
      const request = 'parcel_status'
      return new Promise((resolve, reject) => {
        const params = deepCopy(state.config.api.endpoints[request])
        params.parcel_foreign_id = id
        params.last_status = 'true'
        params.updates_from = state.config.timebar.minDate
        axios.get(state.config.api.url + request + '?' + serialize(params))
          .then(resp => {
            if (resp.data && resp.data.length) {
              const data = resp.data.map(row => {
                if (row.s1product_end_time) {
                  row.date = moment(row.s1product_end_time).format('YYYY-MM-DD')
                }
                return row
              })
              commit('parcel_status', data[0])
              resolve(data[0])
            } else {
              reject()
            }
          }).catch(err => {
            reject(err)
          })
      })
    },
    loadParcelLastRow ({ commit, state }, id) {
      const request = 'parcel_estimate'
      return new Promise((resolve, reject) => {
        const params = deepCopy(state.config.api.endpoints[request])
        params.parcel_foreign_id = id
        params.last_status = 'true'
        params.updates_from = state.config.timebar.minDate
        axios.get(state.config.api.url + request + '?' + serialize(params))
          .then(resp => {
            if (resp.data && resp.data.length) {
              const data = resp.data.map(row => {
                if (row.s1product_end_time) {
                  row.date = moment(row.s1product_end_time).format('YYYY-MM-DD')
                }
                return row
              })
              commit('parcel_last_row', data[0])
              resolve(data[0])
            } else {
              reject()
            }
          }).catch(err => {
            reject(err)
          })
      })
    },
    loadChartData ({ commit, state }, id) {
      return new Promise((resolve, reject) => {
        if (state.chartData && state.chartData.id === id) {
          resolve(state.chartData.data)
        } else {
          const requests = state.config.chart.request
          const result = {
            id: id,
            data: []
          }
          let j = 0
          requests.forEach((request, i) => {
            const params = Object.assign(
              deepCopy(state.config.api.endpoints[request.endpoint]),
              request.params
            )
            params.parcel_foreign_id = id
            params.updates_from = state.config.timebar.minDate
            axios.get(state.config.api.url + request.endpoint + '?' + serialize(params))
              .then(resp => {
                result.data[i] = resp.data.sort((a, b) => {
                  return moment(a[request.dateColumn]).diff(b[request.dateColumn])
                })
                j += 1
                if (j === requests.length) {
                  commit('chartData', result)
                  resolve(result.data)
                }
              }).catch(err => {
                reject(err)
              })
          })
        }
      })
    },
    chartDate ({ commit, state }, show) {
      commit('chartDate', show)
    },
    submitFeedback ({ commit, state }, data) {
      const request = 'feedback'
      return new Promise((resolve, reject) => {
        const url = state.config.api.url + request
        const params = new URLSearchParams()
        Object.keys(data).forEach(key => {
          params.append(key, data[key])
        })
        axios.post(url, params)
          .then(resp => {
            if (resp.data.status === 'ok') {
              resolve(resp.data.status)
            } else {
              reject(resp.data.error)
            }
          }).catch(err => {
            reject(err)
          })
        //setTimeout(() => { resolve('ok') }, 1000)
      })
    },
    about ({ commit, state }, about) {
      commit('about', about)
    },
    parcel ({ commit, state }, row) {
      commit('parcel', row)
    }
  },
  getters: {
    config: state => state.config,
    isLoggedIn: state => !!state.token,
    user: state => state.user,
    token: state => state.token,
    status: state => state.status,
    baseLayer: state => state.baseLayer,
    tileSetConf: state => state.tileSetConf,
    tileSetProperty: state => state.tileSetProperty,
    searchResults: state => state.searchResults,
    parcel: state => state.parcel,
    parcelStatus: state => state.parcelStatus,
    parcelEvents: state => state.parcelEvents,
    parcelLastRow: state => state.parcelLastRow,
    selectedFeature: state => state.feature,
    timebarValue: state => state.timebar_value,
    filter: state => state.filter,
    chartDate: state => state.chartDate,
    chartData: state => state.chartData,
    aboutOpen: state => state.about,
    searchIndex: state => state.searchIndex
  }
})
